import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { BarChart3, Download } from 'lucide-react';

const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

export const ClickReportDialog = ({ endpoint, title, onClose }) => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');
    useEffect(() => {
        setData(null); setErr('');
        if (endpoint) api.get(endpoint).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || 'Could not load report'));
    }, [endpoint]);

    const series = data?.series || [];
    const max = Math.max(1, ...series.map(s => s.clicks));
    const csv = () => {
        const rows = [['Date', 'Clicks'], ...series.map(s => [s.date, s.clicks]), ['Total', data.period_clicks]];
        const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `banner-clicks-${(data.banner?.host_name || data.banner?.in_app_id || 'report').replace(/\s+/g, '-')}.csv`;
        a.click();
    };

    return (
        <Dialog open={!!endpoint} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="click-report-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#1F9D8A]" /> Daily clicks · {title}</DialogTitle>
                    <DialogDescription>
                        {data?.banner?.start_at ? `Running ${new Date(data.banner.start_at).toLocaleDateString('en-IN')} → ${data.banner.end_at ? new Date(data.banner.end_at).toLocaleDateString('en-IN') : 'no end'}` : 'Clicks per day (IST).'}
                    </DialogDescription>
                </DialogHeader>
                {err && <div className="text-sm text-rose-600 py-6 text-center">{err}</div>}
                {!data && !err && <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>}
                {data && (
                    <>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg border border-border p-3"><div className="text-[11px] uppercase text-muted-foreground">Clicks in period</div><div className="font-display text-2xl font-semibold" data-testid="click-report-period-total">{data.period_clicks}</div></div>
                            <div className="rounded-lg border border-border p-3"><div className="text-[11px] uppercase text-muted-foreground">All-time clicks</div><div className="font-display text-2xl font-semibold" data-testid="click-report-all-total">{data.total_clicks}</div></div>
                        </div>
                        {data.period_clicks === 0 && <div className="mt-3 text-xs text-muted-foreground text-center" data-testid="click-report-empty">No clicks yet in this period.</div>}
                        <div className="mt-3 flex items-end gap-[3px] h-40 rounded-lg border border-border p-3 overflow-x-auto" data-testid="click-report-chart">
                            {series.map(s => (
                                <div key={s.date} className="flex flex-col items-center justify-end h-full min-w-[14px] flex-1 group" title={`${fmtDay(s.date)}: ${s.clicks}`}>
                                    <div className="w-full rounded-t bg-[#1F9D8A] group-hover:bg-[#0E2A47] transition-colors" style={{ height: `${(s.clicks / max) * 100}%`, minHeight: s.clicks ? 3 : 1 }} />
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-border text-sm">
                            <table className="w-full"><thead className="bg-secondary/60 text-xs"><tr><th className="text-left px-3 py-1.5">Date</th><th className="text-right px-3 py-1.5">Clicks</th></tr></thead>
                                <tbody>{[...series].reverse().map(s => <tr key={s.date} className="border-t border-border"><td className="px-3 py-1.5">{fmtDay(s.date)}</td><td className="px-3 py-1.5 text-right tabular-nums">{s.clicks}</td></tr>)}</tbody></table>
                        </div>
                        <Button variant="outline" className="mt-3" onClick={csv} data-testid="click-report-csv"><Download className="h-4 w-4 mr-1" /> Download CSV</Button>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
